import { useEffect, useRef, useState } from 'react';
import { saveNotes } from '../util/storage.js';

const useAutosave = function({notes, boardId, boardName, ready, storageAvailable, onError}) {
  const saveVersion = useRef(0);
  const flush = useRef(null);

  useEffect(() => () => flush.current?.(), []);

  const [saveStatus, setSaveStatus] = useState('Loading your board…');

  useEffect(() => {
    if (!ready) return undefined;

    if (!storageAvailable) {
      setSaveStatus('Not saved');

      return undefined;
    }

    // Ignore completions from older saves when a newer edit is pending.
    const version = ++saveVersion.current;

    let pending = true;

    setSaveStatus('Saving…');

    const save = function() {
      if (!pending) return;

      pending = false;
      saveNotes(notes, boardName, boardId).then(() => {
        if (version === saveVersion.current) setSaveStatus('Saved on this device');
      }).catch(() => {
        if (version !== saveVersion.current) return;

        setSaveStatus('Not saved');
        onError('Your latest changes could not be saved. Free some browser storage or remove a large image, then edit a note to retry.');
      });
    };

    flush.current = save;

    // Debounce edits, but flush promptly when the page is hidden or left.
    const timer = setTimeout(save, 250);

    const saveWhenHidden = function() {
      if (document.visibilityState === 'hidden') save();
    };

    window.addEventListener('pagehide', save);
    document.addEventListener('visibilitychange', saveWhenHidden);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('pagehide', save);
      document.removeEventListener('visibilitychange', saveWhenHidden);
    };
  }, [notes, boardId, boardName, ready, storageAvailable, onError]);

  return saveStatus;
};

export default useAutosave;
